import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Zap, ArrowLeft } from 'lucide-react';

export default function ForgotPasswordPage() {
  const { forgotPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await forgotPassword(email);
    if (result.success) setSent(true);
    else setError(result.error);
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-8">
          <Zap className="w-6 h-6 text-[hsl(var(--accent))]" strokeWidth={2.5} />
          <span className="text-2xl font-bold tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>ZipAI</span>
        </div>

        {sent ? (
          <div data-testid="forgot-password-success">
            <h1 className="text-3xl font-black tracking-tight mb-3" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Check your email</h1>
            <p className="text-muted-foreground mb-8">If an account exists with that email, we've sent a password reset link.</p>
            <Link to="/login">
              <Button variant="outline" className="rounded-[4px]" data-testid="back-to-login-btn">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to login
              </Button>
            </Link>
          </div>
        ) : (
          <>
            <h1 className="text-3xl font-black tracking-tight mb-3" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Reset password</h1>
            <p className="text-muted-foreground mb-8">Enter your email and we'll send you a reset link.</p>
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="forgot-password-form">
              {error && <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 px-4 py-3 rounded-md">{error}</div>}
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">Email</Label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required data-testid="forgot-email-input" className="h-11 rounded-[4px]" />
              </div>
              <Button type="submit" disabled={loading} className="w-full h-11 rounded-[4px] font-semibold" data-testid="forgot-submit-btn">
                {loading ? 'Sending...' : 'Send reset link'}
              </Button>
            </form>
            <p className="mt-6 text-sm text-muted-foreground">
              <Link to="/login" className="text-foreground font-medium hover:underline" data-testid="back-login-link">
                <ArrowLeft className="w-3 h-3 inline mr-1" />Back to login
              </Link>
            </p>
          </>
        )}
        <p className="mt-16 text-xs text-muted-foreground/60 tracking-wide text-center">Powered By Cuvano&reg;</p>
      </div>
    </div>
  );
}
